/**
 * ComplaintsPage — Portfolio-level complaint list
 *
 * Shows all complaints across cases managed by the institution.
 * Filters: status, category, date range.
 */
import React, { useState, useEffect } from "react";
import { api } from "../services/api.js";

interface Complaint {
  complaint_id: string;
  case_id: string;
  raised_by: string;
  category: string;
  description: string;
  status: "open" | "in_review" | "resolved";
  officer_notes: Array<{ text: string; author_id: string; created_at: string }>;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: "#dc2626",
  in_review: "#d97706",
  resolved: "#16a34a",
};

const TH: React.CSSProperties = {
  padding: "10px 16px", textAlign: "left", fontWeight: 600,
  fontSize: 12, color: "var(--muted, #64748b)", textTransform: "uppercase",
  letterSpacing: "0.05em", borderBottom: "1px solid var(--border, #e2e8f0)",
  background: "var(--panel, #f8fafc)", whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: "10px 16px", fontSize: 14, color: "var(--ink, #0f172a)",
  borderBottom: "1px solid var(--border, #e2e8f0)", verticalAlign: "middle",
};

interface Props {
  onNavigateToCase?: (caseId: string) => void;
}

export function ComplaintsPage({ onNavigateToCase }: Props): React.ReactElement {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const load = async (currentPage = page, status = filterStatus) => {
    setLoading(true);
    try {
      const res = await api.complaints.list({
        ...(status ? { status } : {}),
        page: currentPage,
        limit: LIMIT,
      });
      setComplaints(res.data as unknown as Complaint[]);
      setTotal(res.total);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyFilter(status: string): void {
    setFilterStatus(status);
    setPage(1);
    void load(1, status);
  }

  function changePage(p: number): void {
    setPage(p);
    void load(p, filterStatus);
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ padding: "32px 40px", maxWidth: 1200, margin: "0 auto", fontFamily: "var(--font-sans, 'Inter', sans-serif)" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>📋</span>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "var(--ink, #0f172a)" }}>
            Support & Complaints
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["", "open", "in_review", "resolved"].map((s) => (
            <button
              key={s || "all"}
              id={`filter-${s || "all"}`}
              onClick={() => applyFilter(s)}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "1px solid var(--border, #e2e8f0)",
                cursor: "pointer",
                fontWeight: filterStatus === s ? 700 : 400,
                fontSize: 13,
                background: filterStatus === s ? "var(--brand, #6366f1)" : "#fff",
                color: filterStatus === s ? "#fff" : "var(--ink, #0f172a)",
                transition: "all 0.15s",
              }}
            >
              {s === "" ? "All" : s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Summary row */}
      <div style={{ marginBottom: 20, color: "var(--muted)", fontSize: 14 }}>
        {total} complaint{total !== 1 ? "s" : ""} {filterStatus ? `with status "${filterStatus.replace(/_/g, " ")}"` : "total"}
      </div>

      {/* Table */}
      <div style={{ borderRadius: 8, border: "1px solid var(--border, #e2e8f0)", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--muted)" }}>Loading…</div>
        ) : complaints.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--muted)" }}>
            No complaints found{filterStatus ? " for this filter" : ""}.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH}>ID</th>
                  <th style={TH}>Case</th>
                  <th style={TH}>Raised By</th>
                  <th style={TH}>Category</th>
                  <th style={TH}>Status</th>
                  <th style={TH}>Notes</th>
                  <th style={TH}>Date</th>
                  <th style={TH}>Action</th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((c, i) => (
                  <tr key={c.complaint_id} style={{ background: i % 2 === 0 ? "#fff" : "var(--panel, #f8fafc)" }}>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{c.complaint_id}</td>
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12, color: "var(--brand, #6366f1)" }}>
                      <button
                        onClick={() => onNavigateToCase?.(c.case_id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, fontFamily: "monospace", fontSize: 12, textDecoration: "underline" }}
                      >
                        {c.case_id}
                      </button>
                    </td>
                    <td style={TD}>{c.raised_by}</td>
                    <td style={{ ...TD, textTransform: "capitalize" }}>{c.category.replace(/_/g, " ")}</td>
                    <td style={TD}>
                      <span style={{
                        background: `${STATUS_COLORS[c.status] ?? "#64748b"}18`,
                        color: STATUS_COLORS[c.status] ?? "#64748b",
                        borderRadius: 4, padding: "3px 9px",
                        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      }}>
                        {c.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td style={{ ...TD, color: "var(--muted)" }}>{c.officer_notes.length}</td>
                    <td style={TD}>{new Date(c.created_at).toLocaleDateString("en-GB")}</td>
                    <td style={TD}>
                      <button
                        onClick={() => onNavigateToCase?.(c.case_id)}
                        style={{
                          padding: "5px 12px",
                          background: "var(--panel, #f8fafc)",
                          border: "1px solid var(--border, #e2e8f0)",
                          borderRadius: 5, cursor: "pointer",
                          fontSize: 12, fontWeight: 500, color: "var(--ink)",
                        }}
                      >
                        View Case →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 20 }}>
          <button onClick={() => changePage(Math.max(1, page - 1))} disabled={page === 1}
            style={{ padding: "6px 12px", border: "1px solid var(--border)", borderRadius: 6, cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.5 : 1, background: "#fff" }}>
            ‹ Prev
          </button>
          <span style={{ padding: "6px 12px", color: "var(--muted)", fontSize: 13 }}>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => changePage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            style={{ padding: "6px 12px", border: "1px solid var(--border)", borderRadius: 6, cursor: page === totalPages ? "not-allowed" : "pointer", opacity: page === totalPages ? 0.5 : 1, background: "#fff" }}>
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}
